"use client";

import React from "react";
import { Bed, Bath, CircleAlert } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
function StayAwhileGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="125200" className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col bg-card rounded-xl shadow-sm overflow-hidden border border-border/50">
            <div className="aspect-[4/3] bg-muted/50 animate-pulse" />
            <div className="p-5 flex flex-col gap-4">
              <div className="h-6 bg-muted/50 animate-pulse rounded-md w-3/4" />
              <div className="flex gap-4">
                <div className="h-4 bg-muted/50 animate-pulse rounded-md w-16" />
                <div className="h-4 bg-muted/50 animate-pulse rounded-md w-16" />
                <div className="h-4 bg-muted/50 animate-pulse rounded-md w-16" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center bg-destructive/5 rounded-xl border border-destructive/20">
        <CircleAlert className="size-8 text-destructive mb-4" />
        <p className="text-foreground font-medium mb-2">Failed to load our room listings.</p>
        <Button variant="outline" onClick={refetch} className="rounded-full">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-muted/20 rounded-xl border border-border/50">
        <p className="text-muted-foreground">No rooms are currently available for booking.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const price = 50;
      const rooms = post.customFields?.bedrooms || Math.floor(Math.random() * 1 + 1);
      const baths = post.customFields?.bathrooms || 1;
      const imageUrl = post.featuredImage || `/img/property_placeholder_${i % 3 + 1}.png`;
      return <div key={post.id} data-index={i} className="group relative flex flex-col bg-card rounded-xl shadow-sm overflow-hidden border border-border/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-2">
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              <img src={imageUrl} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              
              <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-md px-4 py-2 rounded-lg font-bold text-sm shadow-md text-primary border border-border/30">
                ${price} <span className="text-xs font-normal text-muted-foreground">/ night</span>
              </div>

              <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
                <Button asChild variant="default" className="rounded-full font-bold shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="https://www.airbnb.com/rooms/1516286561442761715">Book on Airbnb</Link>
                </Button>
              </div>
            </div>

            <div className="p-6 flex flex-col gap-4">
              <Link to="https://www.airbnb.com/rooms/1516286561442761715" className="hover:text-primary transition-colors">
                <h3 className="font-serif text-xl font-semibold leading-tight line-clamp-1 text-primary">
                  {post.title}
                </h3>
              </Link>
              
              <div className="flex items-center gap-5 text-[14px] text-muted-foreground font-medium">
                <div className="flex items-center gap-2">
                  <Bed className="size-4 text-primary/60" /> 
                  <span>{rooms} Bed</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="size-4 text-primary/60" /> 
                  <span>{baths} Bath</span>
                </div>
              </div>
            </div>
          </div>;
    })}
    </div>;
}
export default function StayAwhile() {
  const wp_query_params = {
    per_page: 6,
    status: "publish",
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query("post", wp_query_params);
  return <section id="our-4-private-rooms" className="w-full bg-background py-24 px-8">
      <div className="max-w-7xl mx-auto flex flex-col gap-12">
        <div className="flex flex-col gap-4 text-center md:text-left">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-primary tracking-tight">
            Our 4 Private Rooms
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-sans leading-relaxed">
            Relax in a comfortable private room in Murrieta. Each suite is meticulously prepared with fresh linens and climate control for your perfect Southern California stay.
          </p>
        </div>

        <PostsProvider wp_query={wp_query}>
          <StayAwhileGrid />
        </PostsProvider>
      </div>
    </section>;
}